import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  message, 
  Popconfirm,
  Typography
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import api from '../../api';

const { Title } = Typography;

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const fetchSuppliers = async (params = {}) => {
    setLoading(true);
    try {
      const response = await api.get('/v1/suppliers', {
        params: {
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
        },
      });
      setSuppliers(response.data.data.map(s => ({...s, key: s.id})));
      setPagination({
        ...pagination,
        current: params.page,
        total: response.data.total,
      });
    } catch (error) {
      message.error('获取供应商列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers({ page: pagination.current, pageSize: pagination.pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (newPagination) => {
    fetchSuppliers({
      page: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const handleSearch = (value) => {
    fetchSuppliers({
      page: 1,
      pageSize: pagination.pageSize,
      search: value,
    });
  };

  const showModal = (record = null) => {
    setEditingRecord(record);
    form.setFieldsValue(record || { name: '', contact_person: '', phone: '', address: '' });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingRecord) {
        await api.put(`/v1/suppliers/${editingRecord.id}`, values);
        message.success('供应商更新成功');
      } else {
        await api.post('/v1/suppliers', values);
        message.success('供应商创建成功');
      }
      handleCancel();
      fetchSuppliers({ page: pagination.current, pageSize: pagination.pageSize });
    } catch (errorInfo) {
      const errorMsg = errorInfo.response?.data?.error || '操作失败';
      message.error(errorMsg);
    }
  };
  
  const handleDelete = async (id) => {
    try {
      await api.delete(`/v1/suppliers/${id}`);
      message.success('供应商删除成功');
      fetchSuppliers({ page: pagination.current, pageSize: pagination.pageSize });
    } catch (error) {
      message.error('删除失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: '供应商名称', dataIndex: 'name', key: 'name' },
    { title: '联系人', dataIndex: 'contact_person', key: 'contact_person' },
    { title: '联系电话', dataIndex: 'phone', key: 'phone' },
    { title: '地址', dataIndex: 'address', key: 'address', ellipsis: true },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at' },
    {
      title: '操作',
      key: 'action',
      align: 'left',
      render: (_, record) => (
        <Space size="small" style={{ marginLeft: -8 }}>
          <Button type="link" size="small" onClick={() => showModal(record)}>编辑</Button>
          <Popconfirm
            title="确定删除此供应商吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>供应商管理</Title>
        <Space>
          <Input.Search
            placeholder="搜索供应商名称或联系人"
            onSearch={handleSearch}
            style={{ width: 250 }}
            allowClear
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            新增供应商
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={suppliers}
        rowKey="id"
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
        size="small"
        bordered
      />

      <Modal
        title={editingRecord ? '编辑供应商' : '新增供应商'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="supplier_form">
          <Form.Item
            name="name"
            label="供应商名称"
            rules={[{ required: true, message: '请输入供应商名称!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="contact_person"
            label="联系人"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label="联系电话"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="address"
            label="地址"
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Suppliers;
